import { useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, fmtVal, errMsg } from "@/lib/api";
import { SourceBadge } from "@/components/meridian/SourceBadge";
import { useApp } from "@/context/AppContext";

function Row({ task, item, missing, reload, ro }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (body, msg) => {
    setBusy(true);
    try { await api.put(`/tasks/${task.id}/facts/${item.key}`, body); toast.success(msg); setVal(""); await reload(); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not save"); }
    setBusy(false);
  };
  const hint = item.format === "pct" ? "e.g. 6.4 or 0.064" : item.format === "usd" ? "e.g. 3,456,000" : "";
  return (
    <div className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`dq-${missing ? "missing" : "review"}-${item.key}`}>
      <div className="w-48">
        <div className="text-sm text-slate-900 font-medium">{item.label}</div>
        <div className={`text-[10px] ${missing ? "text-red-700" : "text-amber-700"}`}>
          {missing ? "Not found in documents - required" : item.conflicts?.length ? "Conflicting values across documents" : item.confidence === "low" ? "Low-confidence extraction" : "Read from a scanned page - verify"}
        </div>
      </div>
      {!missing && (
        <div className="flex-1 min-w-[220px] text-xs">
          <span className="num text-slate-900 font-semibold">{fmtVal(item.value, item.format)}</span> <SourceBadge src={item} />
          {item.snippet && <div className="text-[10px] text-slate-500 num truncate mt-0.5">"{item.snippet}"</div>}
          {item.note && <div className="text-[10px] text-slate-600 italic mt-0.5" data-testid={`dq-note-${item.key}`}>{item.note}</div>}
          {item.conflicts?.map((c, i) => <div key={i} className="text-[10px] text-amber-800">Also found {fmtVal(c.value, item.format)} in {c.doc_name} p.{c.page}</div>)}
        </div>
      )}
      {ro ? <span className="text-[10px] text-slate-600">Analyst action required</span> : <div className={`flex items-center gap-1.5 ${missing ? "flex-1" : ""}`}>
        {!missing && <button data-testid={`dq-confirm-${item.key}`} disabled={busy} onClick={() => send({}, `${item.label} confirmed`)} className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-emerald-500/50 text-emerald-700 hover:bg-emerald-50"><Check className="w-3 h-3" />Confirm</button>}
        <input data-testid={`dq-input-${item.key}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={missing ? `Enter ${item.label.toLowerCase()} ${hint}` : "Correct value"} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 w-44 outline-none focus:border-sky-500" />
        <button data-testid={`dq-save-${item.key}`} disabled={busy || !val.trim()} onClick={() => send({ value: val.trim(), note: missing ? "Entered by analyst - not in document set" : "Corrected by analyst" }, `${item.label} saved`)} className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-sky-600 text-white disabled:opacity-40">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />}Save</button>
      </div>}
    </div>
  );
}

export function DataQuality({ task, reload }) {
  const dq = task.data_quality;
  const { persona } = useApp();
  const ro = !persona?.permissions.includes("lending.edit");
  if (!dq?.extracted) return null;
  if (dq.stale)
    return <div className="panel px-4 py-3 flex items-center gap-2 text-sm text-amber-800 bg-amber-50/70" data-testid="dq-stale"><AlertTriangle className="w-4 h-4" />New documents uploaded since the last extraction. Click Extract to re-run the analysis with them.</div>;
  if (dq.ready)
    return <div className="panel px-4 py-3 flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50/60" data-testid="dq-ready"><CheckCircle2 className="w-4 h-4" />All required figures are sourced or confirmed. Review the Recommendation tab and send to committee.</div>;
  return (
    <div className="panel overflow-hidden border-amber-300/70" data-testid="data-quality-panel">
      <div className="px-4 py-3 border-b border-slate-200 bg-amber-50/70 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-slate-900">Analyst review needed</span>
        <span className="text-[11px] text-slate-600">{[dq.missing.length && `${dq.missing.length} missing`, dq.review.length && `${dq.review.length} to verify`].filter(Boolean).join(" · ")}. Entries are logged with your name.</span>
      </div>
      <div className="divide-y divide-slate-100">
        {dq.missing.map((m) => <Row key={m.key} task={task} item={m} missing reload={reload} ro={ro} />)}
        {dq.review.map((m) => <Row key={m.key} task={task} item={m} reload={reload} ro={ro} />)}
      </div>
    </div>
  );
}
